import { QueryClient } from '@tanstack/react-query';

/**
 * Cache window of an org-wide lookup list (`*ForLookup`).
 *
 * `staleTime` is longer than the 5-minute default because these lists are
 * org-level reference data that changes rarely, while every process detail
 * visit fans out into all four of them. Every write path invalidates them by
 * key prefix — including the chat agent's only entity-creating tool
 * (`createExternalContact`, which invalidates `externalContactKeys.lists()`) —
 * so the window never applies to a change the client made. Accepted ceiling: a
 * write from ANOTHER tab or user is invisible for up to 30 minutes within one
 * SPA session (`refetchOnWindowFocus` is off, and there is no push channel); a
 * reload rebuilds the cache.
 *
 * `gcTime` matches it because these lists are warmed by a route loader, which
 * leaves no observer at all, and then read by surfaces that subscribe on demand
 * — the chat's entity cards subscribe on the first hover. At the default the
 * warm-up would be evicted five minutes into a reading session and every later
 * hover would start from a cold cache.
 */
export const LOOKUP_LIST_CACHE = {
  staleTime: 30 * 60 * 1000,
  gcTime: 30 * 60 * 1000
} as const;

/**
 * TanStack Query Client Configuration
 *
 * This client is used for all server state management in v2.
 * Default settings optimize for a good balance between freshness and performance.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,

      // Don't retry - let agent handle retries (3 with exponential backoff, but only
      // for idempotent methods; see RETRYABLE_METHODS in lib/agent.ts). A query whose
      // queryFn POSTs is therefore not retried at either layer. Accepted: the agent
      // cannot tell a side-effect-free POST query from a mutation, and replay-safety
      // outranks transient-error resilience.
      // Known debt: TanStack knows query-vs-mutation, the interceptor can only infer
      // replay-safety from the verb — retry arguably belongs at this layer instead.
      retry: false,

      // Don't refetch on window focus (can be overwhelming during development)
      refetchOnWindowFocus: false,

      // Refetch stale data when component mounts (allows invalidateQueries to work correctly)
      refetchOnMount: true,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Global error handler for queries
      // Individual queries can override this with their own error handling
      throwOnError: false // Don't throw errors to error boundaries by default
    },
    mutations: {
      // Don't retry mutations - they have side effects, and the agent deliberately
      // never replays non-idempotent methods (see RETRYABLE_METHODS in lib/agent.ts).
      retry: false,

      // Global error handler for mutations
      // Individual mutations should handle their own errors via onError callback
      throwOnError: false // Don't throw errors to error boundaries by default
    }
  }
});
